import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, InputGroup, Card, Badge, Stack, Pagination, Spinner, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faList, faLocationDot, faGlobe, faBuilding, faHeart, faXmark } from '@fortawesome/free-solid-svg-icons';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { LanguageProvider } from '../../core/language-provider';

export default function JobExplore() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [filters, setFilters] = useState({
        country: { id: searchParams.get('country') || '', label: searchParams.get('countryLabel') || '' },
        site: { id: searchParams.get('site') || '', label: searchParams.get('siteLabel') || '' },
        sector: { id: searchParams.get('sector') || '', label: searchParams.get('sectorLabel') || '' },
        role: { id: searchParams.get('role') || '', label: searchParams.get('roleLabel') || '' },
        q: searchParams.get('q') || ''
    });
    const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10) || 1);
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size') || '9', 10) || 9);
    const [jobs, setJobs] = useState(null)
    const [loading, setLoading] = useState(false);
    const [favMap, setFavMap] = useState({});
    const [sectors, setSectors] = useState([]);
    const [countries, setCountries] = useState([]);
    // const [roles, setRoles] = useState([]); // Ruolo nascosto su richiesta
    const [locations, setLocations] = useState([]);
    const [qInput, setQInput] = useState(searchParams.get('q') || '');

    const userInfo = LoginManager.getUserInfo()


    const truncate = (val, max) => {
        if (val === null || val === undefined) return '';
        const s = String(val).trim();
        return s.length > max ? s.slice(0, max).trimEnd() + '…' : s;
    };


    useEffect(() => {
        getSectors();
        getCountries();
        // getRoles(); // Ruolo nascosto su richiesta
        getLocations();
        loadFavorites();
    }, [])


    useEffect(() => {
        getJobsPost();
    }, [filters])


    // Debounce text search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (filters.q !== qInput) {
                setFilters(prev => ({ ...prev, q: qInput }));
                setPage(1);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [qInput]);


    // Persist filters + page in URL
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        const apply = (k, v) => { v ? next.set(k, v) : next.delete(k); };
        apply('q', filters.q);
        apply('country', filters.country.id);
        apply('countryLabel', filters.country.label);
        apply('site', filters.site.id);
        apply('siteLabel', filters.site.label);
        apply('sector', filters.sector.id);
        apply('sectorLabel', filters.sector.label);
        apply('role', filters.role.id);
        apply('roleLabel', filters.role.label);
        apply('page', page > 1 ? String(page) : '');
        apply('size', pageSize !== 9 ? String(pageSize) : '');
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [filters, page, pageSize])


    async function getJobsPost() {
        setLoading(true);
        var constraints = [{
            "fieldName": "status",
            "type": "value",
            "value": "10_OPEN"
        }]
        if (filters.country.id !== "") {
            constraints.push({
                "fieldName": "idCountry",
                "type": "value",
                "value": parseInt(filters.country.id)
            })
        }
        if (filters.role.id !== "") {
            constraints.push({
                "fieldName": "idRole",
                "type": "value",
                "value": parseInt(filters.role.id)
            })
        }
        if (filters.sector.id !== "") {
            constraints.push({
                "fieldName": "idSector",
                "type": "value",
                "value": parseInt(filters.sector.id)
            })
        }
        if (filters.site.id !== "") {
            constraints.push({
                "fieldName": "idSite",
                "type": "value",
                "value": parseInt(filters.site.id)
            })
        }
        var sorting = { "field": "id", "order": "desc" }
        try {
            const res = await DataManager.searchItems('job_post', 1, 1000, sorting, filters.q, ["title"], constraints);
            if (res.success === 1) {
                setJobs(res.body || []);
            } else {
                setJobs([]);
            }
        } catch (err) {
            console.error('getJobsPost error', err);
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }




    async function getSectors() {
        const sorting = { field: 'id', order: 'desc' };
        const res = await DataManager.getItems('sector', 1, 1000, sorting);
        if (res.success === 1) setSectors(res.body || []);
    }

    async function getCountries() {
        const sorting = { field: 'id', order: 'desc' };
        const res = await DataManager.getItems('country', 1, 1000, sorting);
        if (res.success === 1) setCountries(res.body || []);
    }

    // Ruolo nascosto su richiesta
    // async function getRoles() {
    //     const sorting = { field: 'id', order: 'desc' };
    //     const res = await DataManager.getItems('role', 1, 1000, sorting);
    //     if (res.success === 1) setRoles(res.body || []);
    // }

    async function getLocations() {
        const sorting = { field: 'id', order: 'desc' };
        const res = await DataManager.getItems('location', 1, 1000, sorting);
        if (res.success === 1) setLocations(res.body || []);
    }

    const filteredLocations = useMemo(() => {
        if (!filters.country.label) return locations;
        const cl = String(filters.country.label).toLowerCase();
        return (locations || []).filter(l => String(l.country || '').toLowerCase() === cl);
    }, [locations, filters.country.label]);

    async function loadFavorites() {
        const user = LoginManager.getUserInfo();
        if (!user?.id) return;
        const sorting = { field: 'id', order: 'desc' };
        const constraints = [
            { fieldName: 'idUser', type: 'value', value: user.id }
        ];
        const res = await DataManager.searchItems('job_post_favourites', 1, 1000, sorting, '', [], constraints);
        if (res.success === 1) {
            const map = {};
            (res.body || []).forEach(rec => {
                const jobId = rec?.idJobPost?.id || rec?.idJobPost;
                if (jobId) map[jobId] = rec.id;
            });
            setFavMap(map);
        }
    }

    async function toggleFavorite(jobId, e) {
        e?.stopPropagation?.();
        const user = LoginManager.getUserInfo();
        if (!user?.id) {
            navigate('/login', { state: { redirect: window.location.pathname } });
            return;
        }
        try {
            if (favMap[jobId]) {
                const favId = favMap[jobId];
                const del = await DataManager.deleteItem('job_post_favourites', favId);
                if (del.success === 1) {
                    setFavMap(prev => {
                        const copy = { ...prev };
                        delete copy[jobId];
                        return copy;
                    });
                }
                return;
            }
            const ins = await DataManager.insertItem('job_post_favourites', {
                idUser: { id: user.id },
                idJobPost: { id: jobId }
            });
            if (ins.success === 1) {
                const sorting = { field: 'id', order: 'desc' };
                const constraints = [
                    { fieldName: 'idUser', type: 'value', value: user.id },
                    { fieldName: 'idJobPost', type: 'value', value: jobId }
                ];
                const find = await DataManager.searchItems('job_post_favourites', 1, 1, sorting, '', [], constraints);
                const rec = (find.success === 1 && Array.isArray(find.body) && find.body[0]) ? find.body[0] : null;
                if (rec?.id) {
                    setFavMap(prev => ({ ...prev, [jobId]: rec.id }));
                }
            }
        } catch (err) {
            console.error('toggleFavorite error', err);
        }
    }

    const resetFilter = (key) => {
        if (key === 'q') {
            setQInput('');
            setFilters(prev => ({ ...prev, q: '' }));
        } else if (key === 'country') {
            setFilters(prev => ({ ...prev, country: { id: '', label: '' }, site: { id: '', label: '' } }));
        } else {
            setFilters(prev => ({ ...prev, [key]: { id: '', label: '' } }));
        }
        setPage(1);
    }

    const resetAllFilters = () => {
        setQInput('');
        setFilters({
            country: { id: '', label: '' },
            site: { id: '', label: '' },
            sector: { id: '', label: '' },
            role: { id: '', label: '' },
            q: ''
        });
        setPage(1);
    }

    const hasAnyFilter = !!(filters.country.id || filters.site.id || filters.sector.id || filters.role.id || filters.q);

    const totalPages = Math.max(1, Math.ceil(((jobs && jobs.length) || 0) / pageSize));
    const currentPage = Math.min(page, totalPages);
    const slice = jobs && jobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const pageWindow = useMemo(() => {
        const size = 5;
        const half = Math.floor(size / 2);
        let start = Math.max(1, currentPage - half);
        let end = Math.min(totalPages, start + size - 1);
        start = Math.max(1, end - size + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [currentPage, totalPages]);

    const blue = '#0A2B5E';
    const lightBlue = '#2FAAE0';




    return (
        <Container fluid>
            <Container fluid={'md'}>
                {/* Header */}
                <Row className='align-items-center pt-5 pb-3'>
                    <Col>
                        <h1 style={{ color: blue }}>{LanguageProvider.get('pages.jobExplore.title')}</h1>
                    </Col>
                    <Col xs='auto' className='text-muted'>
                        {jobs && jobs.length} {LanguageProvider.get('pages.jobExplore.results')}
                    </Col>
                </Row>

                {/* Filters */}
                <Row className='g-3 mb-3'>
                    <Col lg={2} md={2} sm={6} xs={12}>
                        <InputGroup>
                            <InputGroup.Text style={{ background: 'transparent', borderRight: 'none', borderColor: '#ced4da' }}>
                                <FontAwesomeIcon icon={faGlobe} />
                            </InputGroup.Text>
                            <Form.Select
                                style={{ borderLeft: 'none' }}
                                value={filters.country.id}
                                onChange={e => {
                                    const id = e.target.value;
                                    const label = e.target.selectedOptions[0]?.label || '';
                                    setFilters(prev => ({ ...prev, country: { id, label }, site: { id: '', label: '' } }));
                                    setPage(1);
                                }}
                            >
                                <option value=''>{LanguageProvider.get('pages.jobExplore.filters.country')}</option>
                                {countries && countries.map(c => {
                                    const label = c.name || c.country || c.nation || c.title;
                                    return (
                                        <option key={c.id || label} value={c.id} label={label}>{label}</option>
                                    );
                                })}
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    <Col lg={2} md={2} sm={6} xs={12}>
                        <InputGroup>
                            <InputGroup.Text style={{ background: 'transparent', borderRight: 'none', borderColor: '#ced4da' }}>
                                <FontAwesomeIcon icon={faLocationDot} />
                            </InputGroup.Text>
                            <Form.Select
                                style={{ borderLeft: 'none' }}
                                value={filters.site.id}
                                onChange={e => {
                                    const id = e.target.value;
                                    const label = e.target.selectedOptions[0]?.label || '';
                                    setFilters(prev => ({ ...prev, site: { id, label } }));
                                    setPage(1);
                                }}
                            >
                                <option value=''>{LanguageProvider.get('pages.jobExplore.filters.site')}</option>
                                {filteredLocations.map(l => {
                                    const label = l.city || l.name || l.title;
                                    return (
                                        <option key={l.id || label} value={l.id} label={label}>{label}</option>
                                    );
                                })}
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    <Col lg={2} md={2} sm={6} xs={12}>
                        <InputGroup>
                            <InputGroup.Text style={{ background: 'transparent', borderRight: 'none', borderColor: '#ced4da' }}>
                                <FontAwesomeIcon icon={faList} />
                            </InputGroup.Text>
                            <Form.Select
                                style={{ borderLeft: 'none' }}
                                value={filters.sector.id}
                                onChange={e => {
                                    const id = e.target.value;
                                    const label = e.target.selectedOptions[0]?.label || '';
                                    setFilters(prev => ({ ...prev, sector: { id, label } }));
                                    setPage(1);
                                }}
                            >
                                <option value=''>{LanguageProvider.get('pages.jobExplore.filters.sector')}</option>
                                {sectors && sectors.map(s => {
                                    const label = s.name || s.sector || s.title;
                                    return (
                                        <option key={s.id || label} value={s.id} label={label}>{label}</option>
                                    );
                                })}
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    {/* Filtro Ruolo nascosto su richiesta
                    <Col lg={2} md={2} sm={6} xs={12}>
                        <InputGroup>
                            <InputGroup.Text style={{ background: 'transparent', borderRight: 'none', borderColor: '#ced4da' }}>
                                <FontAwesomeIcon icon={faUser} />
                            </InputGroup.Text>
                            <Form.Select
                                style={{ borderLeft: 'none' }}
                                value={filters.role.id}
                                onChange={e => {
                                    const id = e.target.value;
                                    const label = e.target.selectedOptions[0]?.label || '';
                                    setFilters(prev => ({ ...prev, role: { id, label } }));
                                    setPage(1);
                                }}
                            >
                                <option value=''>{LanguageProvider.get('pages.jobExplore.filters.role')}</option>
                                {roles && roles.map(r => {
                                    const label = r.name || r.role || r.title;
                                    return (
                                        <option key={r.id || label} value={r.id} label={label}>{label}</option>
                                    );
                                })}
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    */}
                    <Col lg={6} md={6} sm={12} xs={12}>
                        <InputGroup>
                            <InputGroup.Text style={{ background: 'transparent', borderRight: 'none', borderColor: '#ced4da' }}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </InputGroup.Text>
                            <Form.Control
                                style={{ borderLeft: 'none' }}
                                placeholder={LanguageProvider.get('pages.jobExplore.filters.searchPlaceholder')}
                                value={qInput}
                                onChange={e => { setQInput(e.target.value); setPage(1); }}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                {/* Applied filters chips */}
                <Row className='mb-4'>
                    <Col>
                        <Stack direction='horizontal' gap={2} className='flex-wrap'>
                            {filters.country?.id && (
                                <Badge bg='' style={{ backgroundColor: lightBlue, color: 'white' }}>
                                    {LanguageProvider.get('pages.jobExplore.filters.labelCountry')} = {filters.country.label}
                                    <span
                                        role='button'
                                        aria-label='Rimuovi filtro Nazione'
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); resetFilter('country'); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resetFilter('country'); } }}
                                        className='ms-2'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </span>
                                </Badge>
                            )}
                            {filters.site?.id && (
                                <Badge bg='' style={{ backgroundColor: lightBlue, color: 'white' }}>
                                    {LanguageProvider.get('pages.jobExplore.filters.labelSite')} = {filters.site.label}
                                    <span
                                        role='button'
                                        aria-label='Rimuovi filtro Sede'
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); resetFilter('site'); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resetFilter('site'); } }}
                                        className='ms-2'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </span>
                                </Badge>
                            )}
                            {filters.sector?.id && (
                                <Badge bg='' style={{ backgroundColor: lightBlue, color: 'white' }}>
                                    {LanguageProvider.get('pages.jobExplore.filters.labelSector')} = {filters.sector.label}
                                    <span
                                        role='button'
                                        aria-label='Rimuovi filtro Settore'
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); resetFilter('sector'); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resetFilter('sector'); } }}
                                        className='ms-2'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </span>
                                </Badge>
                            )}
                            {/* Chip filtro Ruolo nascosto su richiesta
                            {filters.role?.id && (
                                <Badge bg='' style={{ backgroundColor: lightBlue, color: 'white' }}>
                                    {LanguageProvider.get('pages.jobExplore.filters.labelRole')} = {filters.role.label}
                                    <span
                                        role='button'
                                        aria-label='Rimuovi filtro Ruolo'
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); resetFilter('role'); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resetFilter('role'); } }}
                                        className='ms-2'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </span>
                                </Badge>
                            )}
                            */}
                            {filters.q && (
                                <Badge bg='' style={{ backgroundColor: lightBlue, color: 'white' }}>
                                    {LanguageProvider.get('pages.jobExplore.filters.labelSearch')} = {filters.q}
                                    <span
                                        role='button'
                                        aria-label='Rimuovi filtro Ricerca'
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); resetFilter('q'); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); resetFilter('q'); } }}
                                        className='ms-2'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </span>
                                </Badge>
                            )}
                            {hasAnyFilter && (
                                <Button variant='link' size='sm' className='p-0 ms-2' onClick={resetAllFilters}>
                                    Rimuovi tutti
                                </Button>
                            )}
                        </Stack>
                    </Col>
                </Row>

                {/* Cards grid */}
                {loading && jobs === null ? (
                    <div className='text-center py-5'>
                        <Spinner animation='border' role='status' style={{ color: lightBlue }} />
                    </div>
                ) : jobs && jobs.length === 0 ? (
                    <Alert variant='info' className='d-flex align-items-center justify-content-between'>
                        <span>{LanguageProvider.get(hasAnyFilter ? 'pages.jobExplore.noResults' : 'pages.jobExplore.noResultsEmpty')}</span>
                        {hasAnyFilter && (
                            <Button variant='outline-secondary' size='sm' onClick={resetAllFilters}>
                                Rimuovi tutti i filtri
                            </Button>
                        )}
                    </Alert>
                ) : (
                    <Row className='g-4' style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }}>
                        {slice && slice.map(job => (
                            <Col key={job.id} lg={4} md={6} sm={12}>
                                <Card
                                    className='h-100'
                                    style={{ borderColor: '#e6edf5', cursor: 'pointer' }}
                                    onClick={() => navigate(`/job/${job.id}`)}
                                >
                                    <Card.Body>
                                        <div className='d-flex justify-content-between'>
                                            <Card.Title style={{ color: blue, fontWeight: 600 }}>{truncate(job.title, 50)}</Card.Title>
                                            <span
                                                role='button'
                                                aria-label={favMap[job.id] ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                                                onClick={(e) => toggleFavorite(job.id, e)}
                                                style={{ color: favMap[job.id] ? '#E91E63' : '#000', opacity: favMap[job.id] ? 1 : 0.6, cursor: 'pointer' }}
                                            >
                                                <FontAwesomeIcon icon={faHeart} />
                                            </span>
                                        </div>
                                        <div className='mb-2' style={{ color: lightBlue, textTransform: 'uppercase', fontSize: 12 }}>
                                            <FontAwesomeIcon icon={faLocationDot} /> {job.idCountry?.name}{job.idCountry && job.idLocation ? ', ' : ''}{job.idLocation?.city}
                                        </div>
                                        <div className='text-muted richtext-clamp-3' dangerouslySetInnerHTML={{ __html: job.description }}></div>
                                        <Stack direction='horizontal' gap={2} className='flex-wrap mt-2'>
                                            {job.tags && job.tags.map((t, i) => (
                                                <Badge key={i} bg='' style={{ border: '1px solid #dbe7f2', color: blue, backgroundColor: '#f2f7fb', fontWeight: 500 }}>{t.name}</Badge>
                                            ))}
                                        </Stack>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Pagination + page size */}
                <Row className='align-items-center mt-4 mb-5 g-3'>
                    <Col xs={12} md className='text-center text-md-start'>
                        {totalPages > 1 && (
                            <Pagination className='mb-0 justify-content-center justify-content-md-start'>
                                <Pagination.First onClick={() => setPage(1)} disabled={currentPage === 1} />
                                <Pagination.Prev onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
                                {pageWindow[0] > 1 && <Pagination.Ellipsis disabled />}
                                {pageWindow.map(p => (
                                    <Pagination.Item key={p} active={p === currentPage} onClick={() => setPage(p)}>{p}</Pagination.Item>
                                ))}
                                {pageWindow[pageWindow.length - 1] < totalPages && <Pagination.Ellipsis disabled />}
                                <Pagination.Next onClick={() => setPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
                                <Pagination.Last onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} />
                            </Pagination>
                        )}
                    </Col>
                    <Col xs={12} md='auto' className='text-center text-md-end'>
                        {/* Mobile label */}
                        <div className='d-md-none mb-1' style={{ fontSize: 12, color: '#6c757d' }}>Risultati per pagina</div>
                        <InputGroup className='justify-content-center justify-content-md-end'>
                            <InputGroup.Text className='d-none d-md-inline'>{LanguageProvider.get('pages.jobExplore.pagination.perPage')}:</InputGroup.Text>
                            <Form.Select id='results-per-page-select' aria-label='Risultati per pagina' value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }} style={{ maxWidth: 140 }}>
                                {[6, 9, 12, 24, 100].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </Form.Select>
                        </InputGroup>
                    </Col>
                </Row>
            </Container>
        </Container>
    );
}
